/**
 * PasswordManager Component
 * Inline password CRUD operations with AWS Secrets Manager integration
 */

import React, { useState } from 'react';
import { useSubscriptionPassword, usePasswordEditForm, usePasswordStrength } from '../hooks/useSubscriptions';
import { PASSWORD_MANAGER_COPY, storedUiLanguage } from '../i18n/consoleChromeCopy';
import './PasswordManager.css';

interface PasswordManagerProps {
  subscriptionId: string;
  lastRotated?: Date;
  nextRotation?: Date;
  onPasswordUpdated?: () => void;
  disabled?: boolean;
}

export const PasswordManager: React.FC<PasswordManagerProps> = ({
  subscriptionId,
  lastRotated,
  nextRotation,
  onPasswordUpdated,
  disabled = false
}) => {
  const { passwordInfo, showPassword, setShowPassword, isLoading, error, createPassword, updatePassword, deletePassword } =
    useSubscriptionPassword(subscriptionId);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const chrome = PASSWORD_MANAGER_COPY[storedUiLanguage()];

  const handleSubmit = async (username: string, password: string) => {
    const success = passwordInfo ? await updatePassword(password, username) : await createPassword(username, password);
    if (success && onPasswordUpdated) {
      onPasswordUpdated();
    }
    return success;
  };

  const { state, dispatch, validateForm, handleSubmit: submitForm, handleCancel } = usePasswordEditForm(handleSubmit);
  const passwordStrength = usePasswordStrength(state.newPassword);

  const handleDeleteClick = async () => {
    if (!showDeleteConfirm) {
      setShowDeleteConfirm(true);
      return;
    }

    const success = await deletePassword();
    if (success) {
      setShowDeleteConfirm(false);
      if (onPasswordUpdated) {
        onPasswordUpdated();
      }
    }
  };

  // Initial form UI - no password set yet or viewing mode
  if (!state.isEditing && !passwordInfo) {
    return (
      <div className="password-manager password-manager-empty">
        <div className="no-password-set">
          <p>{chrome.noneSet}</p>
          <button
            className="btn btn-primary"
            onClick={() => dispatch({ type: 'START_EDIT' })}
            disabled={disabled || isLoading}
          >
            {chrome.add}
          </button>
        </div>
      </div>
    );
  }

  // Viewing mode - password set, showing masked value
  if (!state.isEditing && passwordInfo) {
    return (
      <div className="password-manager password-manager-viewing">
        <div className="password-display">
          <div className="password-field">
            <label>{chrome.username}</label>
            <div className="password-value">
              <span>{passwordInfo.username || 'Not set'}</span>
            </div>
          </div>

          <div className="password-field">
            <label>Password</label>
            <div className="password-value with-actions">
              <span className="masked-password">••••••••</span>
              <button
                className="btn-icon"
                onClick={() => setShowPassword(!showPassword)}
                title={showPassword ? 'Hide password' : 'Show password'}
                disabled={disabled}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
              <button
                className="btn-icon"
                onClick={() => {
                  navigator.clipboard.writeText(passwordInfo.username || '');
                }}
                title="Copy username"
              >
                📋
              </button>
            </div>
          </div>

          {passwordInfo.lastRotated && (
            <div className="rotation-status">
              <span>Last rotated: {new Date(passwordInfo.lastRotated).toLocaleDateString()}</span>
              {passwordInfo.nextRotation && (
                <span className="next-rotation">
                  Next rotation: {new Date(passwordInfo.nextRotation).toLocaleDateString()}
                </span>
              )}
            </div>
          )}

          <div className="password-actions">
            <button
              className="btn btn-secondary"
              onClick={() => dispatch({ type: 'START_EDIT' })}
              disabled={disabled || isLoading}
            >
              Update Password
            </button>
            <button
              className={`btn btn-danger ${showDeleteConfirm ? 'confirming' : ''}`}
              onClick={handleDeleteClick}
              disabled={disabled || isLoading}
            >
              {showDeleteConfirm ? 'Confirm Delete' : 'Delete Password'}
            </button>
            {showDeleteConfirm && (
              <button
                className="btn btn-outline"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isLoading}
              >
                Cancel
              </button>
            )}
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}
      </div>
    );
  }

  // Edit mode - form for creating/updating password
  return (
    <div className="password-manager password-manager-editing">
      <div className="password-form">
        <div className="form-group">
          <label htmlFor={`username-${subscriptionId}`}>Username / Email</label>
          <input
            id={`username-${subscriptionId}`}
            type="text"
            value={state.username}
            onChange={e => dispatch({ type: 'SET_USERNAME', payload: e.target.value })}
            placeholder="Enter username or email"
            disabled={isLoading}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor={`password-${subscriptionId}`}>Password</label>
          <input
            id={`password-${subscriptionId}`}
            type={showPassword ? 'text' : 'password'}
            value={state.newPassword}
            onChange={e => dispatch({ type: 'SET_PASSWORD', payload: e.target.value })}
            placeholder="Enter new password"
            disabled={isLoading}
            required
          />
          {state.newPassword && (
            <div className="password-strength">
              <div className="strength-bar">
                <div
                  className={`strength-fill strength-${passwordStrength.label.toLowerCase().replace(' ', '-')}`}
                  style={{ width: `${(passwordStrength.score + 1) * 20}%` }}
                ></div>
              </div>
              <span className={`strength-label strength-${passwordStrength.label.toLowerCase().replace(' ', '-')}`}>
                {passwordStrength.label}
              </span>
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor={`confirm-password-${subscriptionId}`}>Confirm Password</label>
          <input
            id={`confirm-password-${subscriptionId}`}
            type={showPassword ? 'text' : 'password'}
            value={state.confirmPassword}
            onChange={e => dispatch({ type: 'SET_CONFIRM_PASSWORD', payload: e.target.value })}
            placeholder="Confirm password"
            disabled={isLoading}
            required
          />
        </div>

        {state.error && <div className="form-error">{state.error}</div>}

        <div className="password-security-note">
          <strong>🔒 Security:</strong> Password is encrypted by AWS KMS before storage. Never logged or displayed in plain text.
        </div>

        <div className="form-actions">
          <button className="btn btn-primary" onClick={submitForm} disabled={isLoading || !state.newPassword}>
            {isLoading ? 'Saving...' : passwordInfo ? 'Update Password' : 'Save Password'}
          </button>
          <button className="btn btn-outline" onClick={handleCancel} disabled={isLoading}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default PasswordManager;
